import React from 'react';
import { Section } from '../layout/Section';
import contentData from '../../data/content.json';
import type { ContentData } from '../../types/content';

const content = contentData as ContentData;

/**
 * ImpressumSection Component
 * 
 * Displays the legally required Impressum (imprint) information
 * in compliance with German Impressumspflicht (§ 5 TMG).
 * 
 * Requirements:
 * - 9.1: Display owner name
 * - 9.2: Display address
 * - 9.3: Display phone number
 * - 9.4: Display email address
 * - 9.5: German Impressumspflicht compliant structure
 */
export const ImpressumSection: React.FC = () => {
  const { ownerName, address, phone, email } = content.impressum;

  return (
    <Section
      id="impressum"
      variant="darker"
      aria-labelledby="impressum-heading"
    >
      {/* Section Header */}
      <div className="text-center mb-12">
        <h2
          id="impressum-heading"
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
        >
          Impressum
        </h2>
        <div className="w-24 h-1 bg-[#c9a84c] mx-auto rounded-full" aria-hidden="true" />
      </div>

      {/* Impressum Content */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#1a1a1a] rounded-xl p-8 space-y-8">

          {/* Angaben gemäß § 5 TMG */}
          <div>
            <h3 className="text-lg font-semibold text-[#c9a84c] mb-4">
              Angaben gemäß § 5 TMG
            </h3>
            <div className="space-y-1 text-gray-300">
              <p className="font-semibold text-white">{ownerName}</p>
              {/* Parse address into lines */}
              {address.split(',').map((line, index) => (
                <p key={index}>{line.trim()}</p>
              ))}
            </div>
          </div>

          {/* Kontakt */}
          <div>
            <h3 className="text-lg font-semibold text-[#c9a84c] mb-4">
              Kontakt
            </h3>
            <div className="flex flex-col gap-2">
              {phone ? (
                <a
                  href={`tel:${phone.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#c9a84c]/30 text-gray-300 hover:text-[#c9a84c] hover:border-[#c9a84c]/60 transition-colors text-sm focus:outline-none focus:underline w-fit"
                  aria-label={`Anrufen: ${phone}`}
                >
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  {phone}
                </a>
              ) : (
                <span className="text-gray-600 italic text-sm">Wird in Kürze ergänzt</span>
              )}
              {email ? (
                <a
                  href={`mailto:${email}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#c9a84c]/30 text-gray-300 hover:text-[#c9a84c] hover:border-[#c9a84c]/60 transition-colors text-sm focus:outline-none focus:underline w-fit"
                  aria-label={`E-Mail: ${email}`}
                >
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  E-Mail
                </a>
              ) : (
                <span className="text-gray-600 italic text-sm">Wird in Kürze ergänzt</span>
              )}
              <a
                href="https://wa.me/491781690543"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#25D366]/30 text-gray-300 hover:text-[#25D366] hover:border-[#25D366]/60 transition-colors text-sm focus:outline-none w-fit"
                aria-label="WhatsApp"
              >
                <svg width={15} height={15} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>
          </div>

          {/* Verantwortlich für den Inhalt */}
          <div>
            <h3 className="text-lg font-semibold text-[#c9a84c] mb-4">
              Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV
            </h3>
            <div className="space-y-1 text-gray-300">
              <p className="font-semibold text-white">{ownerName}</p>
              {address.split(',').map((line, index) => (
                <p key={index}>{line.trim()}</p>
              ))}
            </div>
          </div>

          {/* Haftungsausschluss */}
          <div>
            <h3 className="text-lg font-semibold text-[#c9a84c] mb-4">
              Haftungsausschluss
            </h3>
            <div className="space-y-4 text-gray-400 text-sm leading-relaxed">
              <div>
                <h4 className="text-gray-300 font-medium mb-1">Haftung für Inhalte</h4>
                <p>
                  Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten
                  nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als
                  Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde
                  Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige
                  Tätigkeit hinweisen.
                </p>
              </div>
              <div>
                <h4 className="text-gray-300 font-medium mb-1">Haftung für Links</h4>
                <p>
                  Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen
                  Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.
                  Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der
                  Seiten verantwortlich.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Section>
  );
};
